import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useInventarios } from '../hooks';
import { InventoryStatusBadge } from '../components/InventoryStatusBadge';
import { NovoInventarioWizard } from '../components/NovoInventarioWizard';
import { SCOPE_SUMMARY } from '../components/inventarioEscopo';
import { authService } from '../../../shared/lib/auth';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { formatarData } from '../utils/formatters';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';

interface InventariosPageProps {
  /** Quando true, suprime PageContainer externo (aba em AtivosHomePage). */
  embedded?: boolean;
}

export default function InventariosPage({ embedded = false }: InventariosPageProps = {}) {
  const q = useInventarios({ limit: 100 });
  const user = authService.getCurrentUser();
  const { can } = usePermissions();
  const canManage = can(FEATURES.ativosInventario, 'edit');
  const [wizardOpen, setWizardOpen] = useState(false);
  const navigate = useNavigate();

  const Wrap = ({ children }: { children: ReactNode }) =>
    embedded ? (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Ações anuais de inventário para conferência do patrimônio.</p>
          {canManage && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setWizardOpen(true)}>
              Novo inventário
            </Button>
          )}
        </div>
        {children}
      </div>
    ) : (
      <PageContainer
        title="Inventário patrimonial"
        breadcrumbs={[{ label: 'Ativos', to: '/ativos' }, { label: 'Inventário' }]}
        action={
          canManage && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setWizardOpen(true)}>
              Novo inventário
            </Button>
          )
        }
      >
        {children}
      </PageContainer>
    );

  return (
    <Wrap>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">Nome</th>
              <th className="px-4 py-2 text-left">Escopo</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Progresso</th>
              <th className="px-4 py-2 text-left">Contagens</th>
              <th className="px-4 py-2 text-left">Início</th>
              <th className="px-4 py-2 text-left">Prazo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {q.isLoading && <tr><td colSpan={7} className="py-6 text-center text-sm text-gray-500">Carregando...</td></tr>}
            {q.data && q.data.data.length === 0 && (
              <tr>
                <td colSpan={7} className="p-0">
                  <EmptyState
                    title="Nenhuma ação anual de inventário cadastrada"
                    description={canManage ? 'Comece uma nova ação anual de inventário para conferir o patrimônio.' : undefined}
                    size="compact"
                  />
                </td>
              </tr>
            )}
            {q.data?.data.map((i) => {
              const total = i.progress.total;
              const done = i.progress.found + i.progress.not_found + i.progress.divergent + i.progress.damaged;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <tr key={i.id} className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/ativos/inventarios/${i.id}`)}>
                  <td className="px-4 py-2 text-gray-800 font-medium">{i.name}</td>
                  <td className="px-4 py-2 text-gray-700">{SCOPE_SUMMARY[i.scopeType] ?? i.scopeType}</td>
                  <td className="px-4 py-2"><InventoryStatusBadge status={i.status} /></td>
                  <td className="px-4 py-2 w-40">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200">
                        <div className={`h-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-700">{done}/{total}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-green-700 mr-2">
                      <CheckCircle className="h-3 w-3" /> {i.progress.found}
                    </span>
                    <span className="inline-flex items-center gap-1 text-red-700 mr-2">
                      <XCircle className="h-3 w-3" /> {i.progress.not_found}
                    </span>
                    <span className="inline-flex items-center gap-1 text-orange-700">
                      <AlertTriangle className="h-3 w-3" /> {i.progress.divergent}
                    </span>
                    <span className="ml-2 text-red-700">Danificados: {i.progress.damaged}</span>
                  </td>
                  <td className="px-4 py-2 text-gray-700">{formatarData(i.startedAt)}</td>
                  <td className="px-4 py-2 text-gray-700">{i.deadline ? formatarData(i.deadline) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <NovoInventarioWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(id) => navigate(`/ativos/inventarios/${id}`)}
      />
    </Wrap>
  );
}
